'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import useSWR from 'swr';
import { apiFetch } from '../lib/api';
import { useTranslation } from '../lib/i18n';

type LeadRow = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  email: string | null;
  status: string;
};

const leadLabel = (l: LeadRow): string => {
  const name = [l.firstName, l.lastName].filter(Boolean).join(' ').trim();
  if (name && l.company) return `${name} (${l.company})`;
  return name || l.company || `#${l.id}`;
};

type Props = {
  leadId: number | null;
  leadName: string | null;
  onChange: (next: { leadId: number | null; leadName: string | null }) => void;
  disabled?: boolean;
};

export default function LeadPicker({ leadId, leadName, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setSearch(query.trim()), 250);
    return () => clearTimeout(handle);
  }, [query]);

  const { data: results, isLoading } = useSWR<LeadRow[]>(
    open && search.length > 0
      ? `/api/user/crm/leads?search=${encodeURIComponent(search)}&size=20`
      : null,
  );

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const pick = useCallback((l: LeadRow) => {
    onChange({ leadId: l.id, leadName: leadLabel(l) });
    setQuery('');
    setOpen(false);
  }, [onChange]);

  const clear = useCallback(() => {
    onChange({ leadId: null, leadName: null });
    setQuery('');
  }, [onChange]);

  return (
    <div>
      <label className="block text-xs font-medium text-wood mb-1">{t('calendarLead')}</label>
      {leadId ? (
        <div className="flex items-center justify-between rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark">
          <span className="truncate">{leadName || `#${leadId}`}</span>
          {!disabled && (
            <button type="button" onClick={clear}
              className="text-xs text-wood hover:text-rust transition ml-2">×</button>
          )}
        </div>
      ) : (
        <div ref={boxRef} className="relative">
          <input
            type="text"
            value={query}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            placeholder={t('calendarLeadSearch')}
            className="w-full rounded-rustic border border-wheat bg-cream px-3 py-2 text-sm text-bark focus:outline-none focus:ring-1 focus:ring-moss disabled:opacity-50"
          />
          {open && search.length > 0 && (
            <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-rustic border border-wheat bg-cream shadow-lg">
              {isLoading && <div className="px-3 py-2 text-xs text-wood">…</div>}
              {!isLoading && results?.length === 0 && (
                <div className="px-3 py-2 text-xs text-wood">{t('calendarNoLead')}</div>
              )}
              {results?.map(l => (
                <button key={l.id} type="button" onClick={() => pick(l)}
                  className="block w-full text-left px-3 py-2 text-sm text-bark hover:bg-linen transition">
                  <span className="font-medium">{leadLabel(l)}</span>
                  {l.email && <span className="text-xs text-wood ml-2">{l.email}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
